import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withNoXsrfProtection,
  withXhr,
} from '@angular/common/http';
import { ErrorHandler, inject, NgModule, provideAppInitializer } from '@angular/core';
import { BrowserModule, EVENT_MANAGER_PLUGINS } from '@angular/platform-browser';
import { Router, RouterModule, Routes } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import {
  MissingTranslationHandler,
  provideTranslateService,
  TranslateCompiler,
  TranslateParser,
} from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { AppEffects, AppReducers, metaReducers } from '@reducers/index';
import { MapSettingsLoader, provideMaps } from '@sb/maps';
import { provideRegisterBaseIcons } from '@sb/mgc-icons/register-base-icons.provider';
import { AuthService, MfaService } from '@sb/shared/auth/data-access';
import { featureFlag, featureFlagMatch } from '@sb/shared/feature';
import { CustomTranslateParser, FallbackMissingTranslationHandler } from '@sb/shared/i18n';
import { ThemeService } from '@sb/shared/theme';
import { ButtonComponent } from '@sb/ui/button';
import { SbDialogModule } from '@sb/ui/dialog';
import { LoadingStateComponent } from '@sb/ui/feedback';
import { ICON_REGISTRY, IconRegistry } from '@sb/ui/icon';
import { LogoComponent } from '@sb/ui/logo';
import * as Sentry from '@sentry/angular';
import { Angulartics2Module } from 'angulartics2';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha';
import { PhraseAppCompiler } from 'ngx-translate-phraseapp';

import { environment } from '../environments/environment';
import { AnonymousGuard } from './+anonymous/anonymous-guard.service';
import { PlanWizardComponent } from './+authenticated/+account/billing/plan-wizard/plan-wizard.component';
import { ContractTemplateUploadDialog } from './+authenticated/+employees/templates/contract-template/services/contract-template-upload-dialog';
import { TemplateDialogService } from './+authenticated/+employees/templates/contract-template/services/template-dialog.service';
import { AuthenticatedModule } from './+authenticated/authenticated.module';
import { AccountClosedGuard } from './+errors/account-closed/account-closed-guard.service';
import { AccountClosedComponent } from './+errors/account-closed/account-closed.component';
import { DowngradeGuard } from './+errors/downgrade-steps/downgrade-guard.service';
import { DowngradeComponent } from './+errors/downgrade-steps/downgrade-steps.component';
import { LockedGuard } from './+errors/locked/locked-guard.service';
import { LockedComponent } from './+errors/locked/locked.component';
import { PageNotFoundComponent } from './+errors/page-not-found/page-not-found.component';
import { PausedGuard } from './+errors/paused/paused-guard.service';
import { PausedComponent } from './+errors/paused/paused.component';
import { TrialExpiredGuard } from './+errors/trial-expired/trial-expired-guard.service';
import { TrialExpiredComponent } from './+errors/trial-expired/trial-expired.component';
import { ModalModule } from './+modals/modal.module';
import { OnboardingService } from './+onboarding/onboarding.service';
import { ErrorToastInterceptor } from './api/interceptors/error-toast.interceptor';
import { MetaInterceptor } from './api/interceptors/meta.interceptor';
import { PendingRequestsInterceptor } from './api/interceptors/pending-requests.interceptor';
import { RateLimitBypassInterceptor } from './api/interceptors/rate-limit-bypass.interceptor';
import { RefreshAuthInterceptor } from './api/interceptors/refresh-auth.interceptor';
import { TrackingInterceptor } from './api/interceptors/tracking.interceptor';
import { UnauthorizedRedirectInterceptor } from './api/interceptors/unauthorized-redirect.interceptor';
import { XsrfInterceptor } from './api/interceptors/xsrf.interceptor';
import { XurfInterceptor } from './api/interceptors/xurf.interceptor';
import { AppComponent } from './app.component';
import { AppErrorHandler } from './error-handler';
import { provideApiGatewayOptions } from './providers/api-gateway-options.provider';
import { provideFeatureServiceInitializer } from './providers/feature-service.initializer';
import { provideFormattingContext } from './providers/formatting-context.provider';
import { provideKnowledgeBaseOptions } from './providers/knowledge-base-options.provider';
import { provideMask } from './providers/ngx-mask.provider';
import { providePermissionContext } from './providers/permission-context.provider';
import { accountModuleStore } from './reducers/account-module/account-module.state';
import { ormReducers } from './reducers/orm/orm.providers';
import { AuthServiceBridge } from './services/auth-bridge.service';
import { MfaServiceBridge } from './services/mfa-bridge.service';
import { RefinerService } from './services/refiner.services';
import { VersionUpdateService } from './services/version-update.service';
import { DevToolsComponent } from './shared/dev-tools/dev-tools.component';
import { DomEventsNoChangeDetectionPlugin } from './shared/dom-events-no-change-detection.plugin';
import { LoadingBarComponent } from './shared/loading-bar/loading-bar.component';
import { SharedModule } from './shared/shared.module';
import { featureFlagContextResolver } from './startup/feature-flag-context.resolver';
import { SplashScreenService } from './startup/splash-screen.service';

const routes: Routes = [
  {
    path: 'login',
    canActivate: [AnonymousGuard],
    loadChildren: () => import('@sb/authentication/routes').then((m) => m.authenticationRoutes),
  },
  // Must precede the legacy `signup` route below: the revamped signup lib owns
  // email verification, everything else under /signup stays on the legacy module.
  {
    path: 'signup/verify',
    loadChildren: () => import('@sb/signup/routes').then((m) => m.signupRoutes),
  },
  {
    path: 'signup',
    loadChildren: () => import('./+signup/signup.module').then((m) => m.SignupModule),
  },
  {
    path: 'new-hire/:id',
    canMatch: [featureFlagMatch(featureFlag.TMP_HIRING_SECTIONED_FORM)],
    loadComponent: () =>
      import('./+new-hire/sectioned-form/new-hire-sectioned-form/new-hire-sectioned-form.component').then(
        (c) => c.NewHireSectionedFormComponent,
      ),
  },
  {
    path: 'new-hire/:id',
    loadComponent: () =>
      import('./+new-hire/new-hire-form/new-hire-external-form.component').then((c) => c.NewHireExternalFormComponent),
  },
  {
    path: 'onboarding',
    loadChildren: () => import('./+onboarding/onboarding.module').then((m) => m.OnboardingModule),
  },
  {
    path: 'download-app',
    loadComponent: () => import('./download-app/download-app.component').then((m) => m.DownloadAppComponent),
  },
  {
    path: 'locked',
    component: LockedComponent,
    canActivate: [LockedGuard],
    resolve: { featureFlagContext: featureFlagContextResolver },
  },
  {
    path: 'account-closed',
    component: AccountClosedComponent,
    canActivate: [AccountClosedGuard],
  },
  {
    path: 'trial-expired',
    component: TrialExpiredComponent,
    canActivate: [TrialExpiredGuard],
    resolve: { featureFlagContext: featureFlagContextResolver },
  },
  {
    path: 'downgrade',
    component: DowngradeComponent,
    canActivate: [DowngradeGuard],
    resolve: { featureFlagContext: featureFlagContextResolver },
  },
  {
    path: 'paused',
    component: PausedComponent,
    canActivate: [PausedGuard],
    resolve: { featureFlagContext: featureFlagContextResolver },
  },
  { path: '**', component: PageNotFoundComponent },
];

@NgModule({
  // components and directives
  declarations: [AppComponent, TrialExpiredComponent, AccountClosedComponent, PageNotFoundComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    SharedModule.forRoot(),
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'disabled',
    }),
    Angulartics2Module.forRoot({
      pageTracking: {
        clearIds: true,
        clearQueryParams: true,
      },
    }),
    StoreModule.forRoot(AppReducers, { metaReducers: metaReducers }),
    StoreModule.forFeature('orm', ormReducers),
    StoreModule.forFeature(accountModuleStore),
    EffectsModule.forRoot(AppEffects),
    !environment.production ? StoreDevtoolsModule.instrument({ maxAge: 25, connectInZone: true }) : [],
    ModalModule,
    AuthenticatedModule,
    PlanWizardComponent,
    SbDialogModule,
    RecaptchaV3Module,
    DevToolsComponent,
    LogoComponent,
    ButtonComponent,
    LoadingStateComponent,
    LoadingBarComponent,
  ],
  providers: [
    provideMaps({
      provide: MapSettingsLoader,
      useFactory: () => ({ mapsKey: environment.mapsKey }),
    }),
    { provide: ErrorHandler, useClass: AppErrorHandler },
    { provide: HTTP_INTERCEPTORS, useClass: XurfInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: XsrfInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: RateLimitBypassInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: RefreshAuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: MetaInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorToastInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: UnauthorizedRedirectInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TrackingInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: PendingRequestsInterceptor, multi: true },
    provideRegisterBaseIcons(ICON_REGISTRY),
    { provide: ICON_REGISTRY, useExisting: IconRegistry },
    // Workaround for injecting OnboardingService on app init.
    // We can remove this once we can initialize OnboardingService in a better way.
    provideAppInitializer(() => void inject(OnboardingService)),
    // Eager-instantiate ThemeService so its class-sync effect applies the Aurora scope at startup.
    provideAppInitializer(() => void inject(ThemeService)),
    // Instantiate VersionUpdateService on app init for version polling & gentle refresh.
    provideAppInitializer(() => void inject(VersionUpdateService)),
    // Instantiate SplashScreenService before the initial navigation, so it sees the router
    // event that tells it the first page has rendered.
    provideAppInitializer(() => void inject(SplashScreenService)),
    provideFeatureServiceInitializer(),
    {
      provide: EVENT_MANAGER_PLUGINS,
      useClass: DomEventsNoChangeDetectionPlugin,
      multi: true,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    // Instantiate TraceService so it tracks router changes and parameterizes
    // navigation transaction names by route (otherwise it is provided but never created).
    provideAppInitializer(() => void inject(Sentry.TraceService)),
    AccountClosedGuard,
    LockedGuard,
    TrialExpiredGuard,
    DowngradeGuard,
    PausedGuard,
    RefinerService,
    provideMask(),
    provideHttpClient(withXhr(), withInterceptorsFromDi(), withNoXsrfProtection()),
    provideTranslateService({
      parser: {
        provide: TranslateParser,
        useClass: CustomTranslateParser,
      },
      compiler: {
        provide: TranslateCompiler,
        useClass: PhraseAppCompiler,
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: FallbackMissingTranslationHandler,
      },
    }),
    provideTranslateHttpLoader({ prefix: './locale/', suffix: '.json?v=' + environment.version }),
    provideApiGatewayOptions(),
    provideKnowledgeBaseOptions(),
    provideFormattingContext(),
    providePermissionContext(),
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      // since v21 CDK Overlay has popover feature enabled by default which
      // breaks our current calendar when it's called from the dialog and is rendered inside body
      useValue: { usePopover: false },
    },
    { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.recaptchaSiteKey },
    { provide: AuthService, useClass: AuthServiceBridge },
    { provide: MfaService, useClass: MfaServiceBridge },
    // Root-provided so the contract-template dialogs (created under the root injector by
    // SbDialogService) can resolve it; breaks the upload <-> preview modal import cycle.
    { provide: ContractTemplateUploadDialog, useExisting: TemplateDialogService },
  ],
})
export class AppModule {}
